// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Plain-language definitions for the jargon that appears across the site.
// Used by both the About modal and the inline ℹ popovers via [data-term].

export const GLOSSARY: Record<string, string> = {
  npi: 'The National Pollutant Inventory — Australia\'s public register of industrial emissions and waste transfers, run since 1998 by what is now the Department of Climate Change, Energy, the Environment and Water (DCCEEW).',
  facility: 'A single industrial site (mine, factory, power station, smelter, brewery, abattoir, port, refinery, etc.) that reports its emissions to the NPI. Each has a unique ID and reports separately even if its parent company owns dozens of others.',
  substance: 'One of the 93 chemicals or pollutants tracked by the NPI — examples include carbon monoxide, fine particulate matter (PM2.5), lead, mercury, benzene, ammonia, and total volatile organic compounds.',
  emission: 'A direct release of a substance into the environment — broken into air (smokestacks, vents, fugitive escapes), water (effluent into rivers, oceans, drains) and land (spreading, on-site burial).',
  transfer: 'A substance that leaves the facility but is sent somewhere else — to landfill, sewer, off-site reprocessing or reuse — instead of being released into the environment on-site.',
  anzsic: 'Australian and New Zealand Standard Industrial Classification — a nested code that puts every facility into a division (e.g. Mining), subdivision, group, and class (e.g. 0801 Iron Ore Mining).',
  abn: 'Australian Business Number — the 11-digit identifier the ATO assigns to every registered business; useful for telling apart facilities owned by the same parent company.',
  voc: 'Volatile Organic Compound — a carbon-containing chemical that evaporates easily at room temperature. VOCs include solvents, fuels, and many manufacturing by-products; many contribute to smog or are toxic at low concentrations.',
  pm10: 'Particulate matter smaller than 10 micrometres — coarse dust and soot that lodges in the lungs and aggravates asthma and heart disease.',
  pm25: 'Particulate matter smaller than 2.5 micrometres — finer than PM10, can pass into the bloodstream, and is the single biggest air-pollution health risk in Australia.',
  threshold: 'The NPI doesn\'t make every facility report — only those that exceed a "reporting threshold" for substance use, fuel burn, or emissions. Small factories may operate below the threshold and not appear at all.',
  fugitive: 'An air emission that escapes from leaks, valves, storage tanks, conveyors, or open dust piles rather than a single point source like a stack. Hard to measure, often estimated.',
  point_source: 'An emission from a single, defined point — typically a stack, vent, or pipe — which can be measured directly.',
  reporting_year: 'NPI reporting runs on a financial year (July 1 → June 30). A report year of "2024/25" covers emissions from 1 July 2024 to 30 June 2025.',
  per_capita: 'A figure divided by the population, so you can compare states or regions of very different sizes fairly. The same 1,000 t of emissions matters more in a small state than a large one.'
};

export interface GlossaryTerm {
  key: string;
  label: string;
  definition: string;
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  { key: 'npi', label: 'National Pollutant Inventory (NPI)', definition: GLOSSARY.npi },
  { key: 'facility', label: 'Facility', definition: GLOSSARY.facility },
  { key: 'substance', label: 'Substance', definition: GLOSSARY.substance },
  { key: 'emission', label: 'Emission', definition: GLOSSARY.emission },
  { key: 'transfer', label: 'Transfer', definition: GLOSSARY.transfer },
  { key: 'point_source', label: 'Point-source emission', definition: GLOSSARY.point_source },
  { key: 'fugitive', label: 'Fugitive emission', definition: GLOSSARY.fugitive },
  { key: 'threshold', label: 'Reporting threshold', definition: GLOSSARY.threshold },
  { key: 'reporting_year', label: 'Reporting year', definition: GLOSSARY.reporting_year },
  { key: 'anzsic', label: 'ANZSIC code', definition: GLOSSARY.anzsic },
  { key: 'abn', label: 'ABN', definition: GLOSSARY.abn },
  { key: 'voc', label: 'VOC', definition: GLOSSARY.voc },
  { key: 'pm10', label: 'PM10', definition: GLOSSARY.pm10 },
  { key: 'pm25', label: 'PM2.5', definition: GLOSSARY.pm25 },
  { key: 'per_capita', label: 'Per capita', definition: GLOSSARY.per_capita }
];

// Wrap an existing element so it gets the click-to-define popover styling.
export function decorateGlossaryTerms(root: HTMLElement) {
  const links = root.querySelectorAll<HTMLElement>('[data-term]');
  for (const el of Array.from(links)) {
    if (el.classList.contains('glossary-link')) continue;
    el.classList.add('glossary-link');
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('aria-label', `${el.textContent} — definition`);
  }
}

let tooltipEl: HTMLDivElement | null = null;
function ensureTooltip(): HTMLDivElement {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement('div');
  tooltipEl.className = 'glossary-tooltip';
  tooltipEl.hidden = true;
  document.body.appendChild(tooltipEl);
  return tooltipEl;
}

export function installGlossaryHandlers() {
  ensureTooltip();
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const link = target.closest<HTMLElement>('[data-term]');
    if (link) {
      const term = link.getAttribute('data-term');
      if (!term) return;
      const def = GLOSSARY[term];
      if (!def) return;
      showTooltip(link, def);
      ev.stopPropagation();
      return;
    }
    if (tooltipEl && !tooltipEl.hidden) hideTooltip();
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && tooltipEl && !tooltipEl.hidden) hideTooltip();
  });
}

function showTooltip(anchor: HTMLElement, def: string) {
  const el = ensureTooltip();
  el.textContent = def;
  el.hidden = false;
  const rect = anchor.getBoundingClientRect();
  const top = rect.bottom + window.scrollY + 8;
  el.style.top = `${top}px`;
  // Keep within viewport horizontally.
  const tooltipWidth = 320;
  let left = rect.left + window.scrollX;
  if (left + tooltipWidth > window.innerWidth + window.scrollX - 16) {
    left = window.innerWidth + window.scrollX - tooltipWidth - 16;
  }
  if (left < 8) left = 8;
  el.style.left = `${left}px`;
}

function hideTooltip() {
  if (tooltipEl) tooltipEl.hidden = true;
}
